import { IconButton, Snackbar } from '@material-ui/core';
import {
	CommonStatus,
	OrderStatus,
	useGetOrderLazyQuery,
	useGetOutletCustomerPolicyLazyQuery,
	useGetPrinterOrderItemLazyQuery,
	useGetReceiptPrinterLazyQuery,
	useGetUsersByAccountAndSoftwareQuery,
} from 'generated/graphql';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { printReceiptText } from 'containers/OutletModule/Home/TableSubmenu/BillSettlement/BillReceipt/BillReceiptFunctions';
import { pickDefaultReceiptPrinter } from 'helpers/receiptPrinterTransport';
import {
	kitchenPrintedLocally,
	printTableOrderSlip,
} from 'helpers/printTableOrderSlip';
import { printOrderReceiptRawViaLocalAgent } from 'services/localPrintAgentReceipt';
import CloseIcon from '@material-ui/icons/Close';
import { SocketContext } from 'containers/Socket/SocketContext';

function dedupeOrderItems(items: any[]) {
	const seen = new Set<string>();
	const unique: any[] = [];
	for (const item of items || []) {
		const id = item?.ID;
		if (id) {
			if (seen.has(id)) continue;
			seen.add(id);
		}
		unique.push(item);
	}
	return unique;
}

function isIpAddress(value: unknown): boolean {
	return /^\d{1,3}(\.\d{1,3}){3}$/.test(String(value ?? '').trim());
}

/** Network jobs go to the printer agent by IP only. A Windows name can also match the Zadig USB queue. */
function networkPrinterTarget(...candidates: unknown[]): string | null {
	const values = candidates
		.map(value => String(value ?? '').trim())
		.filter(Boolean);
	return values.find(isIpAddress) ?? null;
}

export const PrinterWrapper = (props: any) => {
	const { socket }: any = useContext(SocketContext);
	const user = JSON.parse(localStorage.getItem('loggedInUser'));
	const printOutletID = localStorage.getItem('latestOutlet');
	const login = localStorage.getItem('login');

	const [open, setOpen] = useState(false);
	const [shouldQueryRun, setShouldQueryRun] = useState(false);
	const jobRef = useRef<any>(null);
	const tryPrintRef = useRef<() => void>(() => undefined);

	const location = useLocation();

	const [
		loadPrinterOrderItem,
		{ loading: getPrinterOrderItemLoading },
	] = useGetPrinterOrderItemLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: data => {
			const job = jobRef.current;
			if (!job || job.started) return;
			job.items = dedupeOrderItems(data?.getPrinterOrderItem || []);
			job.gotItems = true;
			tryPrintRef.current();
		},
		onError: () => {
			const job = jobRef.current;
			if (!job || job.started) return;
			job.items = job.items || [];
			job.gotItems = true;
			tryPrintRef.current();
		},
	});

	const [loadOrder] = useGetOrderLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: data => {
			const job = jobRef.current;
			if (!job || job.started) return;
			const orders = data?.getOrder || [];
			job.order =
				orders.find((row: any) => row?.ID === job.printData?.orderID) ||
				orders[0] ||
				null;
			job.gotOrder = true;
			tryPrintRef.current();
		},
		onError: () => {
			const job = jobRef.current;
			if (!job || job.started) return;
			job.gotOrder = true;
			tryPrintRef.current();
		},
	});

	const path = location?.pathname;
	const isNotAuthPage = !['/login', '/logout', '/kitchen'].includes(path);

	const blockedRoutes = ['/login'];
	useEffect(() => {
		if (login && !blockedRoutes.includes(location.pathname)) {
			setShouldQueryRun(true);
		}
	}, [location.pathname]);

	const {
		data: { getUsersByAccountAndSoftware } = {
			getUsersByAccountAndSoftware: [],
		},
	} = useGetUsersByAccountAndSoftwareQuery({
		fetchPolicy: 'network-only',
		variables: {
			status: CommonStatus.Active,
		},
		skip: !shouldQueryRun,
	});

	const usersRef = useRef(getUsersByAccountAndSoftware);
	usersRef.current = getUsersByAccountAndSoftware;
	const isNotAuthPageRef = useRef(isNotAuthPage);
	isNotAuthPageRef.current = isNotAuthPage;

	const [loadCustomerPolicy] = useGetOutletCustomerPolicyLazyQuery({
		fetchPolicy: 'network-only',
	});
	const [loadReceiptPrinter] = useGetReceiptPrinterLazyQuery({
		fetchPolicy: 'network-only',
	});
	const loadCustomerPolicyRef = useRef(loadCustomerPolicy);
	loadCustomerPolicyRef.current = loadCustomerPolicy;
	const loadReceiptPrinterRef = useRef(loadReceiptPrinter);
	loadReceiptPrinterRef.current = loadReceiptPrinter;

	const handleSocketPrintRef = useRef<(socketPrint: any) => void>(
		() => undefined,
	);

	tryPrintRef.current = () => {
		void runPrint();
	};

	const runPrint = async () => {
		const job = jobRef.current;
		if (!job || job.started || !job.gotItems || !job.gotOrder) {
			return;
		}
		job.started = true;

		const printData = job.printData;
		const orderItems = job.items || [];
		const order = job.order;
		if (!orderItems.length) {
			console.warn('Kitchen print skipped: no order items returned.');
			return;
		}
		if (
			kitchenPrintedLocally({
				orderID: printData?.orderID,
				orderItemIDs: printData?.orderItemIDs,
				orderMode: printData?.orderMode,
				voidQuantity: Number(printData?.voidQuantity) || undefined,
			})
		) {
			return;
		}

		const groups = new Map<string, { info: any; items: any[] }>();
		for (const item of orderItems) {
			const info = item?.menuItem?.kitchenPrinter;
			const name = info?.name || 'NonKitchenPrinter';
			if (!groups.has(name)) groups.set(name, { info, items: [] });
			groups.get(name).items.push(item);
		}

		const voidQuantity = Number(printData.voidQuantity);
		const printedVoidQty =
			Number.isFinite(voidQuantity) && voidQuantity > 0
				? voidQuantity
				: undefined;

		const guestPrint = (async () => {
			const outletID =
				printData?.outletID || localStorage.getItem('latestOutlet');
			if (!outletID) return;
			const [policyRes, printerRes] = await Promise.all([
				loadCustomerPolicyRef.current({ variables: { outletID } }),
				loadReceiptPrinterRef.current({
					variables: { outletID, isDefault: true },
				}),
			]);
			const tableOrderPrint =
				policyRes?.data?.getOutletCustomerPolicy?.[0]?.tableOrderPrint === true;
			if (!tableOrderPrint) return;
			const receiptPrinter = pickDefaultReceiptPrinter(
				printerRes?.data?.getReceiptPrinter,
			);
			if (!receiptPrinter) {
				console.warn('Table order print skipped: no default receipt printer.');
				return;
			}
			await printTableOrderSlip({
				receiptPrinter,
				outletName: printData?.outletName,
				orderData: order,
				tableData: order?.table,
				orderItemData: orderItems,
				orderMode: printData?.orderMode,
				voidQuantity: printedVoidQty,
				reasonCode: printData?.reasonCode,
				remark: printData?.remark,
			});
		})().catch(error => {
			console.warn('Table order print:', (error as Error)?.message ?? error);
		});

		for (const [printerName, group] of groups) {
			const info = group.info;
			const samePrinter = printerName === printData?.printerName;
			const deviceAlignment =
				info?.deviceAlignmentCode ?? printData?.deviceAlignmentCode;
			const networkTarget = networkPrinterTarget(
				info?.connectType ?? (samePrinter ? printData?.connectType : null),
				info?.deviceIP,
				info?.address,
				samePrinter ? printData?.deviceIP : null,
			);

			const isBixolon =
				info?.isSDK === true || (samePrinter && printData?.isSDK === true);

			if (isBixolon) {
				await printReceiptText(
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					printerName,
					'server',
					null,
					'order-receipt',
					null,
					printData?.outletName,
					order,
					order?.table,
					group.items,
					printData?.orderMode,
					null,
					printedVoidQty,
					printData?.reasonCode,
					printData?.remark,
					null,
					null,
					null,
					null,
					null,
					usersRef.current,
					null,
					null,
					null,
				);
			} else if (networkTarget) {
				try {
					await printOrderReceiptRawViaLocalAgent({
						outletName: printData?.outletName,
						orderData: order,
						tableData: order?.table,
						orderItemData: group.items,
						orderMode: printData?.orderMode,
						voidQuantity: printedVoidQty,
						reasonCode: printData?.reasonCode,
						remark: printData?.remark,
						printerName: networkTarget,
						receiptType: 'order-receipt',
						width: deviceAlignment,
					});
				} catch (e) {
					console.warn(
						'Local Print Agent (order-receipt):',
						(e as Error)?.message ?? e,
					);
				}
			}
		}

		await guestPrint;
	};

	handleSocketPrintRef.current = socketPrint => {
		const printData = socketPrint?.printData;
		if (
			socketPrint?.type !== 'POST_PRINT_DATA' ||
			!isNotAuthPageRef.current ||
			!printData?.printerName ||
			printData?.userID !== user?.ID
		) {
			return;
		}

		jobRef.current = {
			printData,
			items: [],
			order: null,
			gotItems: false,
			gotOrder: false,
			started: false,
		};

		loadPrinterOrderItem({
			variables: {
				orderID: printData.orderID,
				orderItemIDs: printData.orderItemIDs,
			},
		});
		loadOrder({
			variables: {
				ID: printData.orderID,
				status: OrderStatus.Open,
				outletID: printOutletID,
			},
		});
	};

	useEffect(() => {
		if (!socket) return;

		const onSocketPrint = socketPrint => {
			try {
				handleSocketPrintRef.current(socketPrint);
			} catch (err) {
				console.error('Error handling print socket:', err);
			}
		};

		socket.off('socketPrint');
		socket.on('socketPrint', onSocketPrint);

		return () => {
			socket.off('socketPrint', onSocketPrint);
		};
	}, [socket]);

	const handleSnackbarClose = () => {
		setOpen(false);
	};

	return (
		<>
			{!getPrinterOrderItemLoading}
			<Snackbar
				className=""
				style={{ top: '10px', right: '10px' }}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				open={open}
				autoHideDuration={3000}
				onClose={() => setOpen(false)}
				ContentProps={{
					'aria-describedby': 'message-id',
					style: { backgroundColor: 'white', color: 'red' },
				}}
				message={<span id="message-id">Listening to print requests...</span>}
				action={
					<IconButton
						key="close"
						aria-label="close"
						color="inherit"
						onClick={handleSnackbarClose}
					>
						<CloseIcon />
					</IconButton>
				}
			/>
		</>
	);
};
